import { Component, OnInit, Input, EventEmitter, Output, signal, OnChanges, TemplateRef, ViewChild, ElementRef, NO_ERRORS_SCHEMA } from '@angular/core';
import { FlowObjectDefinition } from '../../../../models/flow-object.model';
import { FlowDefinition } from '../../../../models/flow.model';
import { ToastrService } from 'ngx-toastr';
import { Enums } from 'src/app/shared/enums';
import { Utils } from '../../../../shared/utils';
import { IBaseOption } from '../../../../models/base.model';
import { ConfigSchema, GatewayRuleOperator, GatewayRuleOperatorDescription } from '../../../../models/config-schema.model';
import { IMPORTS } from './_imports';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';

@Component({
    selector: 'fod-gateway-rules',
    templateUrl: './fod-gateway-rules.component.html',
    styleUrl: './fod-gateway-rules.component.scss',
    standalone: true,
    imports: IMPORTS,
    schemas: [NO_ERRORS_SCHEMA]
})

export class FlowObjectDetailsGatewayRulesComponent implements OnInit, OnChanges {
    // #region [ViewChild]
    @ViewChild('configSchemaRef') configSchemaRef: TemplateRef<HTMLElement>;
    @ViewChild('configSchemaEditorRef') configSchemaEditorRef: ElementRef;
    // #endregion

    // #region [Type props]
    Utils = Utils;
    GatewayRuleOperator = GatewayRuleOperator;
    // #endregion

    // #region [props]
    configSchema = new ConfigSchema();
    gatewayRuleOperatorOptions: IBaseOption[] = [];
    configSchemaDialog: MatDialogRef<HTMLElement, any>;
    // #endregion

    // #region [signals]
    $model = signal(null as FlowObjectDefinition);
    $isReadOnlyMode = signal(false);
    $message = signal(null as string);
    $httpSuccessRule = signal(true);
    $showProperties = signal(false);
    $valueDisabled = signal(false);
    $shouldIgnoreNoValueError = signal(false);
    $shouldIgnoreNonNumberError = signal(false);
    $propertyKey = signal(null as string);
    $ruleOperator = signal(GatewayRuleOperator.Equal);
    $propertyValue = signal(null as any);
    $configSchemaFlat = signal(null as string);
    // #endregion

    // #region [Input/Output]
    @Input() _model: FlowObjectDefinition;
    @Input() _flowDefinition: FlowDefinition;
    @Input() _isReadOnlyMode: boolean;
    @Output() _submitEvent = new EventEmitter<FlowObjectDefinition>();
    @Output() _closeEvent = new EventEmitter<any>();
    // #endregion

    constructor(
        private dialog: MatDialog,
        private toastr: ToastrService
    ) {
        for (let item in GatewayRuleOperator) {
            if (!Number.isNaN(Number.parseInt(item))) {
                this.gatewayRuleOperatorOptions.push({
                    value: +item,
                    description: GatewayRuleOperatorDescription.get(+item)
                });
            }
        }
    }

    ngOnInit() {
        if (this.$model()?.configSchema != null) {
            this.configSchema = JSON.parse(this.$model().configSchema) as ConfigSchema;
            this.initFromModel();
        }

        this.httpSuccessRuleChange();
        this.ruleOperatorChange();
    }

    ngOnChanges() {
        this.$model.set(this._model);
        this.$isReadOnlyMode.set(this._isReadOnlyMode);
    }

    httpSuccessRuleChange(event?) {
        if (event != null) {
            this.$httpSuccessRule.set(event);
        }

        if (this.$httpSuccessRule()) {
            this.$showProperties.set(false);
            this.$propertyKey.set(null);
            this.$ruleOperator.set(null);
            this.$propertyValue.set(null);
        } else {
            this.$showProperties.set(true);
        }
    }

    ruleOperatorChange(event?) {
        if (event != null) {
            this.$ruleOperator.set(event);
        }

        if (this.$ruleOperator() < GatewayRuleOperator.IsTrue || this.$ruleOperator() > GatewayRuleOperator.IsNotEmpty) {
            this.$valueDisabled.set(false);
        } else {
            this.$propertyValue.set(null);
            this.$valueDisabled.set(true);
        }
    }

    showConfigSchema() {
        let tempObject = {
            model: JSON.parse(JSON.stringify(this.$model())) as FlowObjectDefinition,
            configSchema: JSON.parse(JSON.stringify(this.configSchema))
        };
        delete tempObject.model.configSchema;

        this.$configSchemaFlat.set(btoa(encodeURIComponent(JSON.stringify(tempObject))));

        this.configSchemaDialog = this.dialog.open(this.configSchemaRef, {
            minWidth: '800px'
        });

        this.configSchemaDialog.afterOpened().subscribe(() => {
            setTimeout(() => {
                this.configSchemaEditorRef.nativeElement.scrollLeft = 0;
                this.configSchemaEditorRef.nativeElement.scrollTop = 0;
            }, 1);
            setTimeout(() => {
                this.configSchemaEditorRef.nativeElement.style.opacity = '1';
            }, 100);
        });

        this.configSchemaDialog.afterClosed().subscribe(async () => {
            if (this.$isReadOnlyMode()) return;

            try {
                let modalObject = JSON.parse(decodeURIComponent(atob(this.configSchemaEditorRef.nativeElement.value)));
                delete modalObject.model.configSchema;

                // checa se o tipo da origem do conteúdo é o mesmo tipo do conteúdo atual e se a origem não é um FlowDefinition
                if (modalObject.model.typeId != this.$model().typeId || modalObject.model.targetId != null) {
                    this.toastr.error(Enums.Messages.InvalidConfigSchemaSourceType, Enums.Messages.Error, Utils.getToastrErrorOptions());
                    return;
                }

                // checa se o código-fonte foi alterado
                if (JSON.stringify(tempObject) != JSON.stringify(modalObject)) {
                    this.configSchema = JSON.parse(JSON.stringify(modalObject.configSchema)) as ConfigSchema;
                    this.initFromModel();
                }
            } catch (error) {
                this.toastr.error(Enums.Messages.InvalidFlowObjectConfigSchemaSource, Enums.Messages.Error, Utils.getToastrErrorOptions());
                return;
            }
        });
    }

    onSubmit() {
        if (this.$isReadOnlyMode() || this.isInvalidConfiguration()) return false;

        this.configSchema.taskGatewayRules.httpSuccessRule = this.$httpSuccessRule();
        this.configSchema.taskGatewayRules.message = this.$message();
        this.configSchema.taskGatewayRules.propertyKey = this.$propertyKey();
        this.configSchema.taskGatewayRules.ruleOperator = this.$ruleOperator() != null ? Number(this.$ruleOperator()) : null;
        this.configSchema.taskGatewayRules.propertyValue = this.$propertyValue();
        this.configSchema.taskGatewayRules.shouldIgnoreNoValueError = this.$shouldIgnoreNoValueError();
        this.configSchema.taskGatewayRules.shouldIgnoreNonNumberError = this.$shouldIgnoreNonNumberError();

        Utils.updSignal(this.$model, x => {
            x.configSchema = JSON.stringify(this.configSchema);
            return x;
        });

        this._submitEvent.emit(this.$model());
    }

    closeForm() {
        this._closeEvent.emit();
    }

    // #region [===== private methods =====]
    // #endregion

    private initFromModel() {
        this.$httpSuccessRule.set(this.configSchema.taskGatewayRules.httpSuccessRule);
        this.$message.set(this.configSchema.taskGatewayRules.message);
        this.$propertyKey.set(this.configSchema.taskGatewayRules.propertyKey);
        this.$ruleOperator.set(this.configSchema.taskGatewayRules.ruleOperator);
        this.$propertyValue.set(this.configSchema.taskGatewayRules.propertyValue);
        this.$shouldIgnoreNoValueError.set(this.configSchema.taskGatewayRules.shouldIgnoreNoValueError);
        this.$shouldIgnoreNonNumberError.set(this.configSchema.taskGatewayRules.shouldIgnoreNonNumberError);
        this.$showProperties.set(!this.$httpSuccessRule());
    }

    private isInvalidConfiguration() {
        if (
            !this.$httpSuccessRule()
            && (
                Utils.isNullOrEmpty(this.$propertyKey())
                || (
                    Utils.isNullOrEmpty(this.$propertyValue())
                    && (this.$ruleOperator() < GatewayRuleOperator.IsTrue || this.$ruleOperator() > GatewayRuleOperator.IsNotEmpty)
                )
            )
        ) {
            this.toastr.warning(Enums.Messages.NullOrEmpty, Enums.Messages.Error, Utils.getToastrErrorOptions());
            return true;
        }

        if (
            this.$ruleOperator() > GatewayRuleOperator.NotEqual
            && this.$ruleOperator() < GatewayRuleOperator.IsTrue
            && Number.isNaN(Number(this.$propertyValue()))
        ) {
            this.toastr.warning(Enums.Messages.IsNotNumber, Enums.Messages.Error, Utils.getToastrErrorOptions());
            return true;
        }

        return false;
    }
}
